import { FC, useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import HelpTooltip from '@/components/ui/help-tooltip';
import { cn } from '@/lib/utils';
import { Checkbox } from '@/components/ui/checkbox';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import * as Sentry from '@sentry/react';
import {
  testWordPressConnection,
  testWordPressPostTypes,
} from './wordpress-source-utils';
import { TagInput } from '@/components/ui/tag-input';
import type { WordPressExclusionRule } from '@/types/source-types';

export interface WordPressSiteFormState {
  siteId: string;
  label: string;
  baseUrl: string;
  scheduleCron: string;
  cronEnabled: boolean;
  username: string;
  password: string;
  hasCredentials: boolean;
  isPasswordDirty: boolean;
  useAuth: boolean;
  postTypes: string[];
  perPage: number;
  exclusionRules: WordPressExclusionRule[];
  userContentField: string;
}

export type WordPressSiteFormErrors = Partial<
  Pick<
    WordPressSiteFormState,
    'label' | 'baseUrl' | 'scheduleCron' | 'username' | 'password'
  >
> & {
  form?: string;
};

interface WordPressSiteFormProps {
  site: WordPressSiteFormState;
  onChange: (patch: Partial<WordPressSiteFormState>) => void;
  errors: WordPressSiteFormErrors;
  disabled?: boolean;
  isEditing?: boolean;
}

const buildPasswordPlaceholder = (hasCredentials: boolean) =>
  hasCredentials ? '••••••' : '';

export const WordPressSiteForm: FC<WordPressSiteFormProps> = ({
  site,
  onChange,
  errors,
  disabled = false,
  isEditing = false,
}) => {
  const showAuthFields = site.useAuth;
  const showEditablePasswordField =
    site.useAuth &&
    (!isEditing || site.isPasswordDirty || !site.hasCredentials);

  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  // Local draft for the per-page input so the user can freely type multi-digit
  // values (e.g. "50"). Clamping to [10,100] on every keystroke would rewrite
  // the first digit "5" to 10 before the second digit lands, leaving the field
  // editable only via the arrow buttons. We clamp on blur instead.
  const [perPageDraft, setPerPageDraft] = useState(String(site.perPage));

  // Re-seed when the upstream value changes (switching sites, reset, etc.).
  useEffect(() => {
    setPerPageDraft(String(site.perPage));
  }, [site.perPage]);

  const commitPerPage = () => {
    const val = parseInt(perPageDraft, 10);
    const clamped = isNaN(val) ? 10 : Math.min(100, Math.max(10, val));
    if (clamped !== site.perPage) onChange({ perPage: clamped });
    setPerPageDraft(String(clamped));
  };

  const handleTestConnection = async () => {
    setIsTestingConnection(true);
    setTestResult(null);

    try {
      const result = await testWordPressConnection(
        site.baseUrl,
        site.useAuth ? site.username : undefined,
        site.useAuth && showEditablePasswordField ? site.password : undefined,
      );

      setTestResult(result);
    } catch (error) {
      console.error('WordPress connection test failed:', error);
      Sentry.captureException(error, {
        extra: { context: 'wordpress-test-connection', siteId: site.siteId },
      });
      setTestResult({
        success: false,
        message: 'An unexpected error occurred',
      });
    } finally {
      setIsTestingConnection(false);
    }
  };

  const [isTestingPostTypes, setIsTestingPostTypes] = useState(false);
  const [postTypesTestResult, setPostTypesTestResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  useEffect(() => {
    setPostTypesTestResult(null);
  }, [site.postTypes]);

  const handleTestPostTypes = async () => {
    setIsTestingPostTypes(true);
    setPostTypesTestResult(null);

    try {
      const result = await testWordPressPostTypes(
        site.baseUrl,
        site.postTypes,
        site.useAuth ? site.username : undefined,
        site.useAuth && showEditablePasswordField ? site.password : undefined,
      );

      setPostTypesTestResult(result);
    } catch (error) {
      console.error('WordPress post types test failed:', error);
      Sentry.captureException(error, {
        extra: { context: 'wordpress-test-post-types', siteId: site.siteId },
      });
      setPostTypesTestResult({
        success: false,
        message: 'An unexpected error occurred',
      });
    } finally {
      setIsTestingPostTypes(false);
    }
  };

  const handleBaseUrlBlur = () => {
    // Auto-populate label from hostname if label is empty
    if (!site.label.trim() && site.baseUrl.trim()) {
      try {
        const hostname = new URL(site.baseUrl).hostname;
        onChange({ label: hostname });
      } catch {
        // Not a valid URL yet, skip auto-populate
      }
    }
  };

  return (
    <div className='space-y-4'>
      {/* Site Label */}
      <div className='space-y-2'>
        <div className='flex items-center gap-2'>
          <Label htmlFor={`wp-site-label-${site.siteId}`}>Site label</Label>
          <HelpTooltip label='Site label help'>
            A human-friendly name for this WordPress site. Auto-populated from
            the URL if left empty.
          </HelpTooltip>
        </div>
        <Input
          id={`wp-site-label-${site.siteId}`}
          type='text'
          placeholder='e.g., Main Blog'
          value={site.label}
          onChange={(event) => onChange({ label: event.target.value })}
          disabled={disabled}
          maxLength={200}
          error={errors.label}
        />
      </div>

      {/* Base URL */}
      <div className='space-y-2'>
        <Label htmlFor={`wp-base-url-${site.siteId}`}>WordPress base URL</Label>
        <Input
          id={`wp-base-url-${site.siteId}`}
          type='url'
          placeholder='https://example.com'
          value={site.baseUrl}
          onChange={(event) => onChange({ baseUrl: event.target.value })}
          onBlur={handleBaseUrlBlur}
          disabled={disabled}
          error={errors.baseUrl}
        />
        <p className='text-xs text-muted-foreground'>
          Enter the public base URL for your WordPress site.
        </p>
      </div>

      {/* Schedule */}
      <div className='space-y-2'>
        <Label htmlFor={`wp-schedule-${site.siteId}`}>Scrape cadence</Label>
        <Input
          id={`wp-schedule-${site.siteId}`}
          placeholder='0 0 * * *'
          value={site.scheduleCron}
          onChange={(event) => onChange({ scheduleCron: event.target.value })}
          disabled={disabled}
          error={errors.scheduleCron}
        />
        <p className='text-xs text-muted-foreground'>
          Standard Unix cron format (5 fields). Example: 0 0 * * * (daily at
          midnight). Validate at{' '}
          <a
            href='https://crontab.guru/'
            target='_blank'
            rel='noopener noreferrer'
            className='text-primary underline hover:text-primary/80'
          >
            crontab.guru
          </a>
        </p>
      </div>

      {/* Cron Enabled */}
      <div className='space-y-2'>
        <div className='flex items-center gap-2'>
          <Checkbox
            id={`wp-cron-enabled-${site.siteId}`}
            checked={site.cronEnabled}
            onCheckedChange={(checked) =>
              onChange({ cronEnabled: Boolean(checked) })
            }
            disabled={disabled}
          />
          <Label
            htmlFor={`wp-cron-enabled-${site.siteId}`}
            className='font-medium'
          >
            Enable scheduled ingestion
          </Label>
        </div>
        <p className='text-xs text-muted-foreground ml-6'>
          When enabled, content will be automatically ingested based on the
          schedule above.
        </p>
      </div>

      {/* Auth Checkbox */}
      <div className='space-y-2'>
        <div className='flex items-center gap-2'>
          <Checkbox
            id={`wp-use-auth-${site.siteId}`}
            checked={site.useAuth}
            onCheckedChange={(checked) =>
              onChange({
                useAuth: Boolean(checked),
                ...(checked
                  ? {}
                  : {
                      username: '',
                      password: '',
                      isPasswordDirty: false,
                    }),
              })
            }
            disabled={disabled}
          />
          <Label htmlFor={`wp-use-auth-${site.siteId}`} className='font-medium'>
            Use authentication
          </Label>
          {site.hasCredentials && !site.useAuth && (
            <span className='text-xs text-muted-foreground'>
              Stored credentials will be cleared on save.
            </span>
          )}
        </div>
      </div>

      {/* Auth Fields */}
      {showAuthFields && (
        <>
          <div className='space-y-2'>
            <Label
              className='label-required'
              htmlFor={`wp-username-${site.siteId}`}
            >
              Username
            </Label>
            <Input
              id={`wp-username-${site.siteId}`}
              placeholder='Username required when password is set'
              value={site.username}
              onChange={(event) => onChange({ username: event.target.value })}
              disabled={disabled}
              error={errors.username}
            />
          </div>

          <div className='space-y-2'>
            <div className='flex items-center justify-between'>
              <Label
                className='label-required'
                htmlFor={`wp-password-${site.siteId}`}
              >
                Password
              </Label>
              {isEditing && site.hasCredentials && !site.isPasswordDirty && (
                <Button
                  type='button'
                  variant='ghost'
                  size='sm'
                  onClick={() =>
                    onChange({
                      isPasswordDirty: true,
                      password: '',
                    })
                  }
                  disabled={disabled}
                >
                  Update password
                </Button>
              )}
            </div>
            <Input
              id={`wp-password-${site.siteId}`}
              type='password'
              placeholder={buildPasswordPlaceholder(site.hasCredentials)}
              value={showEditablePasswordField ? site.password : '********'}
              onChange={(event) =>
                onChange({
                  password: event.target.value,
                  isPasswordDirty: true,
                })
              }
              disabled={disabled || !showEditablePasswordField}
              error={errors.password}
            />
            <p className='text-xs text-muted-foreground'>
              Password transmitted once; stored securely in AWS SSM.
            </p>
          </div>
        </>
      )}

      {/* Post Types */}
      <div className='space-y-2'>
        <div className='flex items-center gap-2'>
          <Label>WordPress post types</Label>
          <HelpTooltip label='Post types help'>
            Specify which post types to ingest (e.g., 'posts', 'pages',
            'custom_type'). Leave empty to ingest only standard posts. Common
            types: posts, pages.
          </HelpTooltip>
        </div>
        <TagInput
          value={site.postTypes}
          onChange={(value) => onChange({ postTypes: value })}
          placeholder='Type and press Enter (comma also separates)...'
          disabled={disabled}
        />
        <p className='text-xs text-muted-foreground'>
          Optional: Enter specific post types to limit ingestion (default: posts
          only)
        </p>
        <p className='text-xs text-amber-600 dark:text-amber-400'>
          Warning: removing a post type here deletes its already-indexed content
          from search on the next reconcile.
        </p>
      </div>

      {/* Items per page */}
      <div className='space-y-2'>
        <div className='flex items-center gap-2'>
          <Label htmlFor={`wp-per-page-${site.siteId}`}>Items per page</Label>
          <HelpTooltip label='Pagination help'>
            Number of items to fetch per API request (10-100). Higher values
            reduce API calls and speed up ingestion, but may timeout on slow
            servers. Default: 10
          </HelpTooltip>
        </div>
        <Input
          id={`wp-per-page-${site.siteId}`}
          type='number'
          min={10}
          max={100}
          inputMode='numeric'
          value={perPageDraft}
          onChange={(e) => setPerPageDraft(e.target.value)}
          onBlur={commitPerPage}
          disabled={disabled}
          className='w-32'
        />
        <p className='text-xs text-muted-foreground'>
          Recommended: 100 for faster ingestion
        </p>
      </div>

      {/* User content field override */}
      <div className='space-y-2'>
        <div className='flex items-center gap-2'>
          <Label htmlFor={`wp-user-content-field-${site.siteId}`}>
            User content field
          </Label>
          <HelpTooltip label='User content field help'>
            Advanced: when ingesting WordPress users (authors), use this custom
            payload field as the content body instead of the standard short
            `description`. For example, sites that register a `long_description`
            field can name it here. Leave empty to use `description`.
          </HelpTooltip>
        </div>
        <Input
          id={`wp-user-content-field-${site.siteId}`}
          type='text'
          placeholder='e.g., long_description'
          value={site.userContentField}
          onChange={(event) =>
            onChange({ userContentField: event.target.value })
          }
          disabled={disabled}
          maxLength={100}
          className='w-64'
        />
        <p className='text-xs text-muted-foreground'>
          Optional: only applies to the "users" post type. Falls back to the
          standard `description` field when empty.
        </p>
      </div>

      {/* Test Post Types Button */}
      {site.postTypes.length > 0 && (
        <div className='space-y-2'>
          <Button
            type='button'
            variant='outline'
            size='sm'
            onClick={handleTestPostTypes}
            disabled={
              disabled ||
              isTestingPostTypes ||
              !site.baseUrl.trim() ||
              site.postTypes.length === 0
            }
            className={cn(
              'w-full sm:w-auto',
              postTypesTestResult?.success && 'border-green-500 text-green-700',
            )}
          >
            {isTestingPostTypes ? (
              <>
                <Loader2 className='size-4 mr-2 animate-spin' />
                Verifying post types...
              </>
            ) : postTypesTestResult ? (
              postTypesTestResult.success ? (
                <>
                  <CheckCircle2 className='size-4 mr-2 text-green-600' />
                  Post types exist
                </>
              ) : (
                <>
                  <XCircle className='size-4 mr-2 text-destructive' />
                  Verification failed
                </>
              )
            ) : (
              'Verify post types'
            )}
          </Button>

          {postTypesTestResult && !postTypesTestResult.success && (
            <p className='text-sm text-destructive'>
              {postTypesTestResult.message}
            </p>
          )}
        </div>
      )}

      {/* Test Connection Button */}
      <div className='space-y-2 pt-2'>
        <Button
          type='button'
          variant='outline'
          size='sm'
          onClick={handleTestConnection}
          disabled={disabled || isTestingConnection || !site.baseUrl.trim()}
          className={cn(
            'w-full sm:w-auto',
            testResult?.success && 'border-green-500 text-green-700',
          )}
        >
          {isTestingConnection ? (
            <>
              <Loader2 className='size-4 mr-2 animate-spin' />
              Testing connection...
            </>
          ) : testResult ? (
            testResult.success ? (
              <>
                <CheckCircle2 className='size-4 mr-2 text-green-600' />
                Connection successful
              </>
            ) : (
              <>
                <XCircle className='size-4 mr-2 text-destructive' />
                Connection failed
              </>
            )
          ) : (
            'Test connection'
          )}
        </Button>

        {testResult && !testResult.success && (
          <p className='text-sm text-destructive'>{testResult.message}</p>
        )}
      </div>

      {/* Auth status line */}
      <div className='text-xs text-muted-foreground'>
        {site.hasCredentials
          ? site.useAuth
            ? 'Credentials stored'
            : 'Stored credentials will be removed when you save changes'
          : site.useAuth
            ? 'No credentials on file'
            : 'Authentication disabled'}
      </div>
    </div>
  );
};
